import {Icon, Spinner, SpinnerSize, Tab, Tabs} from "@blueprintjs/core";
import { useApiEffect } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import { AffixRequestApi, ERequestState, ERequestType, RequestApi } from "../../../../api";
import {
  BackButton,
  ConditionTab,
  LinkedSubFilesTab,
  PageBase,
  RequestHistoryTab,
  RequestStickyMenu,
  VerificationTab,
} from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { MailLogListPage } from "../../../mail-log/MailLogListPage";
import { BillTab } from "../../BillTab";
import { AffixRequestDetail } from "./AffixDetailPage";

export interface IAffixRequestItemPageProps {}

export const AffixRequestItemPage: React.FunctionComponent<IAffixRequestItemPageProps> = (props) => {
  const { id, tab, state, type } = useParams<{
    id: string;
    tab: string;
    state: string;
    type: string;
  }>();
  const history = useHistory();
  const api = useApiService(AffixRequestApi);
  const requestApi = useApiService(RequestApi);
  const { currentLanguage } = useGlobalData();
  const { t } = useTl();

  const getRequestType = React.useCallback(() => {
    switch (type) {
      case "kennelImport":
        return ERequestType.KennelImportRequest;
      case "kennelOwnership":
        return ERequestType.KennelOwnershipChangeRequest;
      case "newKennel":
        return ERequestType.NewKennelRequest;
      case "kennelExport":
        return ERequestType.KennelExportRequest;
      case "kennelNameChange":
        return ERequestType.KennelNameChangeRequest;
      default:
        return null;
    }
  }, [type]);

  const [requestInfo, requestInfoLoading, , refreshRequestInfo] = useApiEffect(() => {
    const requestType = getRequestType();
    if (requestType) {
      return requestApi.getRequestInfo(requestType, +id, 0, currentLanguage.languageId);
    }
    return null;
  }, [requestApi, id, currentLanguage]);

  const closeRequest = React.useCallback(async () => {
    await api.closeAffixRequest(requestInfo?.parentRequestId);
  }, [api, requestInfo]);

  const refreshRequest = React.useCallback(() => {
    refreshRequestInfo();
  }, [refreshRequestInfo]);

  return (
    <PageBase
      breadCrumbs={[
        {
          text: t(ETLCodes.AffixRequests),
          route: ERoutes.affixRequest,
        },
        {
          text:
            +id > 0 ? (
              requestInfoLoading ? (
                <Spinner size={SpinnerSize.SMALL} />
              ) : (
                requestInfo?.requestNumber
              )
            ) : (
              t(ETLCodes.New)
            ),
        },
      ]}
      actionHistory={
        +id > 0 &&
        !requestInfoLoading && {
          tlCode: ETLCodes.ActionHistoryAffix,
          name: requestInfo?.requestNumber,
          id: +id,
        }
      }
      stickyContent={() => {
        if (requestInfo?.requestId > 0) {
          return (
            <RequestStickyMenu
              requestId={requestInfo?.parentRequestId}
              requestInfo={requestInfo}
              refreshFunc={refreshRequest}
              closeFunc={closeRequest}
            />
          );
        } else {
          return null;
        }
      }}
    >
      <Tabs
        id="TabsAffixRequest"
        onChange={(newTabId) => history.push(`${ERoutes.affixRequest}/${+id}/${newTabId}/${state}/${type}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.affixRequest} />
        <Tab
          id="detail"
          title={
            <div>
              {t(ETLCodes.Detail)} {requestInfo?.hasWarning && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={<AffixRequestDetail editMode={state === "edit"} affixRequestId={+id} requestInfo={requestInfo} />}
        />
        <Tab
          id="verification"
          title={t(ETLCodes.Verifications)}
          disabled={+id <= 0}
          panel={
            requestInfo ? (
              <VerificationTab
                requestId={requestInfo?.parentRequestId}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.VerificationsToObtainBilledStatus}
              />
            ) : null
          }
        />
        <Tab
          id="conditions"
          title={t(ETLCodes.Conditions)}
          disabled={
            +id <= 0 ||
            requestInfo?.requestTypeCode === ERequestType.KennelNameChangeRequest ||
            requestInfo?.requestTypeCode === ERequestType.KennelExportRequest ||
            requestInfo?.requestTypeCode === ERequestType.KennelOwnershipChangeRequest
          }
          panel={
            requestInfo && (
              <ConditionTab
                requestId={requestInfo.parentRequestId}
                requestState={requestInfo.requestStateCode}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.Conditions}
              />
            )
          }
        />
        <Tab
          id="subrequests"
          title={t(ETLCodes.SubRequests, {
            count: requestInfoLoading ? `` : `(${requestInfo?.subRequestCount ?? "0"})`,
          })}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <LinkedSubFilesTab requestInfo={requestInfo} editMode={state === "edit"} />
            )
          }
          disabled
        />
        <Tab
          id="billing"
          title={t(ETLCodes.Billing)}
          panel={
            <BillTab
              requestId={requestInfo?.parentRequestId}
              disabled={requestInfo?.requestStateCode !== ERequestState.CANCELLED}
            />
          }
          disabled={+id <= 0 || requestInfo?.requestStateCode === ERequestState.REQUESTED}
        />
        <Tab
          id="history"
          title={t(ETLCodes.History)}
          panel={requestInfoLoading ? <Spinner /> : <RequestHistoryTab requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
        <Tab
          id="mailing"
          title={t(ETLCodes.Email)}
          panel={requestInfoLoading ? <Spinner /> : <MailLogListPage requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
      </Tabs>
    </PageBase>
  );
};
